/**
 * MachineCash — наличные в автомате по гроссбуху Core (heartbeat `cash`):
 * купюры в стекере, монеты в боксе, тубы по номиналам — и сверка с тем, что
 * сервер ждёт по наличным заказам с последней инкассации. Ниже — история
 * инкассаций (события admin_action{cashout}).
 *
 * До 29.10.2026 единственной цифрой был «Cashbox: N Kč» — счётчик в памяти
 * службы с последнего рестарта, смешивавший купюры, тубы и тест-монеты.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  getCoreHeartbeat, getExpectedCashbox, getCashoutHistory,
  type CoreHeartbeat, type ExpectedCashbox, type CashoutRecord,
} from '../api/client';

function czk(centi: number | null | undefined): string {
  if (typeof centi !== 'number') return '—';
  return `${Math.round(centi / 100)} Kč`;
}

function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = iso.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
  const t = Date.parse(s);
  return Number.isNaN(t) ? iso : new Date(t).toLocaleString();
}

export function MachineCash({ machineId }: { machineId: number }) {
  const [hb, setHb] = useState<CoreHeartbeat | null>(null);
  const [expected, setExpected] = useState<ExpectedCashbox | null>(null);
  const [history, setHistory] = useState<CashoutRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getCoreHeartbeat(machineId),
      getExpectedCashbox(machineId).catch(() => null),
      getCashoutHistory(machineId, 30).catch(() => null),
    ])
      .then(([h, e, hist]) => { setHb(h); setExpected(e); setHistory(hist); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [machineId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const cash = hb?.status?.cash ?? null;
  const tubes = hb?.status?.coin?.tubes ?? [];

  return (
    <div className="card mb-6">
      <div className="card-header flex items-center justify-between" style={{ gap: 8 }}>
        <span>Cash in machine</span>
        <button className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: '0.8rem' }} onClick={load} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </button>
      </div>
      <div className="card-body">
        {error && <p className="text-secondary">Error: {error}</p>}
        {!error && !cash && (
          <p className="text-muted">
            The kiosk has not reported its cash ledger yet (older Core build, or no heartbeat).
            {typeof hb?.status?.cashboxCenti === 'number' && (
              <> Legacy counter since service start: <b>{czk(hb.status.cashboxCenti)}</b>.</>
            )}
          </p>
        )}
        {cash && (
          <>
            {!cash.reconciled && (
              <div style={{ background: '#fff4d6', border: '1px solid #e8c36a', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: '0.88rem' }}>
                <b>Not reconciled.</b> The cash count has not been verified since {when(cash.sinceUtc)} — only money accepted
                since then is shown. The first confirmed cashout in the service menu (stacker + cashbox emptied) becomes the baseline.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '8px 18px', fontSize: '0.9rem' }}>
              <div>Notes (stacker): <b>{czk(cash.billsCenti)}</b></div>
              <div>
                Coins (cashbox): <b>{czk(cash.coinsBoxCenti)}</b>
                {cash.coinsTestCenti > 0 && <span className="text-muted"> (incl. coin test {czk(cash.coinsTestCenti)})</span>}
              </div>
              <div>To collect: <b>{czk(cash.collectCenti)}</b></div>
              <div>Coins in tubes (change): <b>{czk(cash.tubesCenti)}</b></div>
              <div>Total inside: <b>{czk(cash.totalCenti)}</b></div>
              <div className="text-muted">Counted since: {when(cash.sinceUtc)}</div>
              {tubes.length > 0 && (
                <div style={{ gridColumn: '1 / -1' }} className="text-muted">
                  Tubes: {tubes.map((t) => `${t.czk} Kč×${t.count}`).join(' · ')}
                </div>
              )}
              {expected && (
                <div style={{ gridColumn: '1 / -1' }} title="Σ cash orders (cash_collected) since the last cashout, from the server journal">
                  Expected by orders since last cashout: <b>{expected.expectedCashboxCzk} Kč</b>
                  {cash.reconciled && (
                    <span className="text-muted">
                      {' '}· difference vs. kiosk ledger: {Math.round(cash.collectCenti / 100) - expected.expectedCashboxCzk} Kč
                    </span>
                  )}
                  {expected.needsCashout && <span style={{ color: '#b45309' }}> · time to collect</span>}
                </div>
              )}
            </div>
          </>
        )}

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Cashout history</div>
          {history === null ? (
            <p className="text-muted">No history available.</p>
          ) : history.length === 0 ? (
            <p className="text-muted">No cashouts recorded yet.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>When</th><th>Collected</th><th>Notes</th><th>Coins (box)</th><th>Left in tubes</th><th>Since</th><th>Key</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r) => (
                    <tr key={r.at + (r.keyId ?? '')}>
                      <td style={{ whiteSpace: 'nowrap' }}>{when(r.at)}</td>
                      <td><b>{czk(r.amountCenti)}</b>{r.reconciled === false ? <span className="text-muted" title="First cashout after the update: counted only since the date in the Since column"> *</span> : null}</td>
                      <td>{czk(r.billsCenti)}</td>
                      <td>{czk(r.coinsBoxCenti)}</td>
                      <td>{czk(r.tubesCenti)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{when(r.sinceUtc)}</td>
                      <td className="text-muted">{r.keyId ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
